import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { API_BASE } from '../config/api';
import { resolveSession } from '../lib/session';
import { useSessionUser } from '../SessionContext';
import './Reports.css';

const emptyTotals = {
  uniqueAgents: 0,
  totalEvents: 0,
  loginEvents: 0,
  logoutEvents: 0,
  openSessions: 0,
  missingLoginEvents: 0,
  totalLoginDurationSeconds: 0,
  openDurationSeconds: 0,
};

const DEFAULT_SQL = `SELECT
  eventtime,
  agentid,
  status,
  reason,
  inqueue,
  direction,
  cmsuserid,
  cmsusername
FROM agentstate
WHERE eventtime >= :start_date::date
  AND eventtime < (:end_date::date + INTERVAL '1 day')
ORDER BY eventtime ASC, agentid ASC;`;

const toIsoDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const todayIso = () => toIsoDate(new Date());

const monthStartIso = () => {
  const date = new Date();
  date.setDate(1);
  return toIsoDate(date);
};

const normalizeText = (value) => String(value ?? '').trim();

const readNumber = (source, keys, fallback = 0) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') {
      return Number(value) || fallback;
    }
  }
  return fallback;
};

const formatDate = (value) => {
  const text = normalizeText(value);
  if (!text) return '';
  const [year, month, day] = text.slice(0, 10).split('-');
  if (!year || !month || !day) return text;
  return `${Number(month)}/${Number(day)}/${year}`;
};

const formatDateTime = (value) => {
  const text = normalizeText(value);
  if (!text) return '';
  const date = new Date(text);
  if (!Number.isNaN(date.getTime())) {
    return `${formatDate(date.toISOString().slice(0, 10))} ${date.toLocaleTimeString('en-US', { hour12: true })}`;
  }
  return text;
};

const formatNumber = (value) => Math.round(Number(value) || 0).toLocaleString();

const formatHms = (seconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remSeconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remSeconds).padStart(2, '0')}`;
};

const formatTime = (value) => {
  const text = normalizeText(value);
  if (!text) return '';
  const parts = text.split(':');
  if (parts.length >= 2) {
    const date = new Date(`2000-01-01T${text}`);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
    }
  }
  return text;
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const normalizeTotals = (source = {}) => ({
  uniqueAgents: readNumber(source, ['uniqueAgents', 'unique_agents', 'UniqueAgents']),
  totalEvents: readNumber(source, ['totalEvents', 'total_events', 'TotalEvents']),
  loginEvents: readNumber(source, ['loginEvents', 'login_events', 'LoginEvents']),
  logoutEvents: readNumber(source, ['logoutEvents', 'logout_events', 'LogoutEvents']),
  openSessions: readNumber(source, ['openSessions', 'open_sessions', 'OpenSessions']),
  missingLoginEvents: readNumber(source, ['missingLoginEvents', 'missing_login_events', 'MissingLoginEvents']),
  totalLoginDurationSeconds: readNumber(source, ['totalLoginDurationSeconds', 'total_login_duration_seconds', 'TotalLoginDurationSeconds']),
  openDurationSeconds: readNumber(source, ['openDurationSeconds', 'open_duration_seconds', 'OpenDurationSeconds']),
});

const normalizeRow = (source = {}) => ({
  date: normalizeText(source.date ?? source.reportDate ?? source.report_date),
  time: normalizeText(source.time ?? source.reportTime ?? source.report_time),
  eventTime: normalizeText(source.eventTime ?? source.event_time ?? source.EventTime),
  agentId: normalizeText(source.agentId ?? source.agent_id ?? source.AgentId),
  cmsUserName: normalizeText(source.cmsUserName ?? source.cms_user_name ?? source.CmsUserName),
  eventType: normalizeText(source.eventType ?? source.event_type ?? source.EventType ?? source.status ?? source.Status),
  loginDurationSeconds: readNumber(source, ['loginDurationSeconds', 'login_duration_seconds', 'LoginDurationSeconds']),
  missingLogin: Boolean(source.missingLogin ?? source.missing_login ?? source.MissingLogin),
});

const normalizeGroup = (source = {}) => ({
  agentId: normalizeText(source.agentId ?? source.agent_id ?? source.AgentId),
  cmsUserName: normalizeText(source.cmsUserName ?? source.cms_user_name ?? source.CmsUserName),
  agentLabel: normalizeText(source.agentLabel ?? source.agent_label ?? source.AgentLabel),
  initialLoginTime: normalizeText(source.initialLoginTime ?? source.initial_login_time ?? source.InitialLoginTime),
  eventCount: readNumber(source, ['eventCount', 'event_count', 'EventCount']),
  loginCount: readNumber(source, ['loginCount', 'login_count', 'LoginCount']),
  logoutCount: readNumber(source, ['logoutCount', 'logout_count', 'LogoutCount']),
  openSession: Boolean(source.openSession ?? source.open_session ?? source.OpenSession),
  openSessionStartedAt: normalizeText(source.openSessionStartedAt ?? source.open_session_started_at ?? source.OpenSessionStartedAt),
  openDurationSeconds: readNumber(source, ['openDurationSeconds', 'open_duration_seconds', 'OpenDurationSeconds']),
  totalLoginDurationSeconds: readNumber(source, ['totalLoginDurationSeconds', 'total_login_duration_seconds', 'TotalLoginDurationSeconds']),
  rows: Array.isArray(source.rows) ? source.rows.map(normalizeRow) : [],
});

const buildReportPath = (filters) => {
  const params = new URLSearchParams();
  params.set('startDate', filters.startDate);
  params.set('endDate', filters.endDate);
  if (filters.agent) params.set('agent', filters.agent);
  if (filters.direction) params.set('direction', filters.direction);
  return `/reports/agent-login-logout-report?${params.toString()}`;
};

const fetchJson = async (path) => {
  const response = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  const text = await response.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok || json?.ok === false || json?.success === false) {
    const message = json?.error || json?.message || text || `Request failed: ${response.status}`;
    if (response.status === 404 && String(message).toLowerCase().includes('route')) {
      throw new Error('Laravel report API route missing. Register GET /api/reports/agent-login-logout-report in routes/api.php.');
    }
    throw new Error(message);
  }
  return json;
};

const AgentLoginLogoutReport = () => {
  const navigate = useNavigate();
  const { user: sessionUser } = useSessionUser();
  const { username, roleName } = useMemo(() => resolveSession(sessionUser), [sessionUser]);
  const [filters, setFilters] = useState({
    startDate: monthStartIso(),
    endDate: todayIso(),
    agent: '',
    direction: '',
  });
  const [report, setReport] = useState({
    totals: emptyTotals,
    agentGroups: [],
    reportInterval: '',
    siteName: 'CC',
    source: 'agentstate',
    dataIssues: [],
    eventSuggestions: [],
    sqlOutput: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadReport = useCallback(async (nextFilters = filters) => {
    if (!nextFilters.startDate || !nextFilters.endDate) {
      setError('Start Date and End Date are required.');
      return;
    }
    if (nextFilters.startDate > nextFilters.endDate) {
      setError('Start Date cannot be after End Date.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const json = await fetchJson(buildReportPath(nextFilters));
      const root = json?.data || json || {};
      setReport({
        totals: normalizeTotals(root.totals || {}),
        agentGroups: Array.isArray(root.agentGroups) ? root.agentGroups.map(normalizeGroup) : [],
        reportInterval: root.reportInterval || '',
        siteName: normalizeText(root.siteName || 'CC') || 'CC',
        source: normalizeText(root.source || 'agentstate'),
        dataIssues: Array.isArray(root.dataIssues) ? root.dataIssues : [],
        eventSuggestions: Array.isArray(root.eventSuggestions) ? root.eventSuggestions : [],
        sqlOutput: root.sqlOutput || '',
      });
    } catch (err) {
      setReport((prev) => ({
        ...prev,
        totals: emptyTotals,
        agentGroups: [],
      }));
      setError(err?.message || 'Failed to load Agent Login / Logout Report.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    document.body.setAttribute('data-app', 'reports');
    return () => document.body.removeAttribute('data-app');
  }, []);

  useEffect(() => {
    loadReport(filters);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value.trimStart() }));
  };

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const rows = [
      ['Agent Login / Logout'],
      ['Report Interval', report.reportInterval || `${filters.startDate} - ${filters.endDate}`],
      ['Site Name', report.siteName],
      ['Agent Filter', filters.agent || 'All'],
      ['Direction Filter', filters.direction || 'All'],
      [],
      ['Total Events', report.totals.totalEvents],
      ['Unique Agents', report.totals.uniqueAgents],
      ['Login Events', report.totals.loginEvents],
      ['Logout Events', report.totals.logoutEvents],
      ['Open Sessions', report.totals.openSessions],
      ['Total Login Duration', formatHms(report.totals.totalLoginDurationSeconds)],
      [],
      ['Date', 'Time', 'Event Type', 'Login Duration', 'Agent'],
    ];

    report.agentGroups.forEach((group) => {
      rows.push([]);
      rows.push([`Agent Name - ID: ${group.agentLabel}`]);
      rows.push([`Initial Login for Report Interval: ${formatDateTime(group.initialLoginTime) || 'Not found'}`]);
      group.rows.forEach((row) => {
        rows.push([
          formatDate(row.date),
          formatTime(row.time),
          row.eventType,
          formatHms(row.loginDurationSeconds),
          group.agentLabel,
        ]);
      });
      if (group.openSession) {
        rows.push(['', '', `Open session since ${formatDateTime(group.openSessionStartedAt)}`, formatHms(group.openDurationSeconds), group.agentLabel]);
      }
    });

    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), 'Agent Login Logout');
    XLSX.writeFile(workbook, `agent-login-logout-${filters.startDate}-${filters.endDate}.xlsx`);
  };

  return (
    <div className="cdn-report-shell agent-login-report-shell">
      <header className="cdn-report-topbar no-print">
        <div>
          <strong>SLACC</strong>
          <span>Sindh Legal Advisory Call Center</span>
        </div>
        <div>
          <h1>Agent Login / Logout</h1>
          <p>Database report</p>
        </div>
        <div className="cdn-report-user-actions">
          <span>{username ? `${username}${roleName ? ` - ${roleName}` : ''}` : 'Report Module'}</span>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports')}>CDN Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/dnis-statistics')}>DNIS Report</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/daily-call-report')}>Daily Call</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-state-report')}>Agent State</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/reports/agent-performance-report')}>Performance</button>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/backoffice')}>Back Office</button>
          <button type="button" className="cdn-btn danger" onClick={() => navigate('/logout')}>Logout</button>
        </div>
      </header>

      <section className="cdn-filter-panel no-print">
        <div className="agent-login-filter-grid">
          <label><span>Start Date</span><input type="date" name="startDate" value={filters.startDate} onChange={updateField} /></label>
          <label><span>End Date</span><input type="date" name="endDate" value={filters.endDate} onChange={updateField} /></label>
          <label><span>Agent</span><input type="text" name="agent" value={filters.agent} onChange={updateField} placeholder="ID or name" /></label>
          <label>
            <span>Direction</span>
            <select name="direction" value={filters.direction} onChange={updateField}>
              <option value="">All Direction</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </label>
          <div className="cdn-filter-actions">
            <button type="button" className="cdn-btn primary" onClick={() => loadReport(filters)} disabled={loading}>
              {loading ? 'Loading' : 'Show'}
            </button>
            <button type="button" className="cdn-btn success" onClick={exportExcel} disabled={!report.agentGroups.length}>Excel</button>
            <button type="button" className="cdn-btn soft" onClick={() => window.print()} disabled={!report.agentGroups.length}>PDF</button>
          </div>
        </div>
        {error ? <div className="cdn-alert">{error}</div> : null}
        {report.dataIssues?.length ? (
          <div className="dnis-data-issues">
            {report.dataIssues.map((issue) => <p key={issue}>{issue}</p>)}
          </div>
        ) : null}
        {report.eventSuggestions?.length ? (
          <div className="dnis-data-issues agent-login-suggestions">
            {report.eventSuggestions.map((issue) => <p key={issue}>{issue}</p>)}
          </div>
        ) : null}
      </section>

      <main className="cdn-report-paper agent-login-report-paper">
        <section className="cdn-report-heading">
          <h2>Agent Login / Logout</h2>
          <p><strong>Report Interval:</strong> {report.reportInterval || `${filters.startDate} 00:00:00 - ${filters.endDate} 23:59:59`}</p>
          <p><strong>Site Name:</strong> {report.siteName}</p>
        </section>

        <section className="daily-call-kpi-grid agent-login-kpi-grid no-print">
          <article><span>Total Events</span><strong>{formatNumber(report.totals.totalEvents)}</strong></article>
          <article><span>Agents</span><strong>{formatNumber(report.totals.uniqueAgents)}</strong></article>
          <article><span>Login</span><strong>{formatNumber(report.totals.loginEvents)}</strong></article>
          <article><span>Logout</span><strong>{formatNumber(report.totals.logoutEvents)}</strong></article>
          <article><span>Open Sessions</span><strong>{formatNumber(report.totals.openSessions)}</strong></article>
          <article><span>Login Duration</span><strong>{formatHms(report.totals.totalLoginDurationSeconds)}</strong></article>
        </section>

        <section className="agent-login-table-section">
          <table className="agent-login-report-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Event Type</th>
                <th>Login Duration</th>
              </tr>
            </thead>
            <tbody>
              {report.agentGroups.length ? report.agentGroups.map((group) => (
                <React.Fragment key={`${group.agentId}-${group.cmsUserName}`}>
                  <tr className="agent-login-agent-row">
                    <td colSpan={4}>Agent Name - ID: {group.agentLabel}</td>
                  </tr>
                  <tr className="agent-login-initial-row">
                    <td colSpan={4}>Initial Login for Report Interval: {formatDateTime(group.initialLoginTime) || 'Not found'}</td>
                  </tr>
                  {group.rows.map((row, index) => (
                    <tr key={`${group.agentId}-${row.eventTime}-${index}`} className={row.missingLogin ? 'agent-login-warning-row' : ''}>
                      <td>{formatDate(row.date)}</td>
                      <td>{formatTime(row.time)}</td>
                      <td>{row.eventType}{row.missingLogin ? ' (missing login)' : ''}</td>
                      <td>{formatHms(row.loginDurationSeconds)}</td>
                    </tr>
                  ))}
                  {group.openSession ? (
                    <tr className="agent-login-warning-row">
                      <td></td>
                      <td></td>
                      <td>Open session since {formatDateTime(group.openSessionStartedAt)}</td>
                      <td>{formatHms(group.openDurationSeconds)}</td>
                    </tr>
                  ) : null}
                </React.Fragment>
              )) : (
                <tr>
                  <td colSpan={4} className="cdn-empty">
                    {loading ? 'Loading report...' : 'No report data found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </main>

      <section className="cdn-sql-panel no-print">
        <details>
          <summary>Database SQL Output</summary>
          <pre>{report.sqlOutput || DEFAULT_SQL}</pre>
        </details>
      </section>
    </div>
  );
};

export default AgentLoginLogoutReport;
